import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Target,
  Award,
  Gift,
  DollarSign,
  TrendingUp,
  Edit,
  Plus,
  Users2,
  Check,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { StatCard, formatCents } from "./shared";

export function EngagementDashboard({ challenges, achievements, referralCodes, ambassadors }: any) {
  const { toast } = useToast();
  const [showCreateChallenge, setShowCreateChallenge] = useState(false);
  const [showCreateAchievement, setShowCreateAchievement] = useState(false);

  const ambassadorMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "approved" | "denied" }) => {
      const res = await apiRequest("PATCH", `/api/ambassadors/${id}`, { status });
      return res.json();
    },
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/ambassadors"] });
      toast({
        title: status === "approved" ? "Ambassador Approved" : "Ambassador Denied",
        description: `Ambassador application has been ${status}.`,
      });
    },
    onError: () => {
      toast({ title: "Action Failed", description: "Could not update ambassador status.", variant: "destructive" });
    },
  });

  const [challengeTitle, setChallengeTitle] = useState("");
  const [challengeDescription, setChallengeDescription] = useState("");
  const [challengeType, setChallengeType] = useState("health");
  const [challengeGoalType, setChallengeGoalType] = useState("streak");
  const [challengeGoalValue, setChallengeGoalValue] = useState("");
  const [challengeGoalUnit, setChallengeGoalUnit] = useState("days");
  const [challengeRewardAmount, setChallengeRewardAmount] = useState("");
  const [challengeStartDate, setChallengeStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [challengeEndDate, setChallengeEndDate] = useState(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]);

  const [achievementName, setAchievementName] = useState("");
  const [achievementDescription, setAchievementDescription] = useState("");
  const [achievementCategory, setAchievementCategory] = useState("health");
  const [achievementRequirementType, setAchievementRequirementType] = useState("count");
  const [achievementRequirementValue, setAchievementRequirementValue] = useState("");
  const [achievementRequirementUnit, setAchievementRequirementUnit] = useState("");
  const [achievementRewardAmount, setAchievementRewardAmount] = useState("0");
  const [achievementBadgeLevel, setAchievementBadgeLevel] = useState("bronze");
  const [achievementDisplayOrder, setAchievementDisplayOrder] = useState("0");

  const createChallengeMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiRequest("POST", "/api/challenges", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/challenges"] });
      setShowCreateChallenge(false);
      setChallengeTitle("");
      setChallengeDescription("");
      setChallengeType("health");
      setChallengeGoalType("streak");
      setChallengeGoalValue("");
      setChallengeGoalUnit("days");
      setChallengeRewardAmount("");
      setChallengeStartDate(new Date().toISOString().split("T")[0]);
      setChallengeEndDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]);
      toast({ title: "Challenge Created", description: "New challenge has been added." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const createAchievementMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiRequest("POST", "/api/achievements", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/achievements"] });
      setShowCreateAchievement(false);
      setAchievementName("");
      setAchievementDescription("");
      setAchievementCategory("health");
      setAchievementRequirementType("count");
      setAchievementRequirementValue("");
      setAchievementRequirementUnit("");
      setAchievementRewardAmount("0");
      setAchievementBadgeLevel("bronze");
      setAchievementDisplayOrder("0");
      toast({ title: "Achievement Created", description: "New achievement has been added." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const activeChallenges = challenges.filter((c: any) => c.isActive);
  const totalParticipants = challenges.reduce((sum: number, c: any) => sum + (c.currentParticipants || 0), 0);
  const activeReferralCodes = referralCodes.filter((r: any) => r.isActive);
  const totalReferralBonus = referralCodes.reduce((sum: number, r: any) => sum + (r.timesUsed || 0) * (r.referrerBonus || 0), 0);

  return (
    <>
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          title="Active Challenges"
          value={activeChallenges.length}
          description={`${totalParticipants} total participants`}
          icon={Target}
          color="purple"
        />
        <StatCard
          title="Achievement Types"
          value={achievements.length}
          description="Configured achievements"
          icon={Award}
          color="yellow"
        />
        <StatCard
          title="Active Referral Codes"
          value={activeReferralCodes.length}
          description={`${referralCodes.reduce((s: number, r: any) => s + (r.timesUsed || 0), 0)} total uses`}
          icon={Gift}
          color="green"
        />
        <StatCard
          title="Referral Bonuses Paid"
          value={formatCents(totalReferralBonus)}
          description="Total paid to referrers"
          icon={DollarSign}
          color="blue"
        />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Health Challenges
            </CardTitle>
            <Dialog open={showCreateChallenge} onOpenChange={setShowCreateChallenge}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="button-new-challenge">
                  <Plus className="w-4 h-4 mr-2" />
                  New Challenge
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="dialog-create-challenge">
                <DialogHeader>
                  <DialogTitle>Create New Challenge</DialogTitle>
                  <DialogDescription>Configure a new health challenge for users</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Challenge Title</Label>
                    <Input
                      placeholder="e.g., 30-Day Medication Adherence"
                      value={challengeTitle}
                      onChange={(e) => setChallengeTitle(e.target.value)}
                      data-testid="input-challenge-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      placeholder="Describe the challenge..."
                      value={challengeDescription}
                      onChange={(e) => setChallengeDescription(e.target.value)}
                      data-testid="input-challenge-description"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select value={challengeType} onValueChange={setChallengeType}>
                        <SelectTrigger data-testid="select-challenge-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="health">Health</SelectItem>
                          <SelectItem value="savings">Savings</SelectItem>
                          <SelectItem value="engagement">Engagement</SelectItem>
                          <SelectItem value="referral">Referral</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Goal Type</Label>
                      <Select value={challengeGoalType} onValueChange={setChallengeGoalType}>
                        <SelectTrigger data-testid="select-challenge-goal-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="streak">Streak (Days)</SelectItem>
                          <SelectItem value="count">Count</SelectItem>
                          <SelectItem value="amount">Amount ($)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Goal Value</Label>
                      <Input
                        type="number"
                        placeholder="30"
                        value={challengeGoalValue}
                        onChange={(e) => setChallengeGoalValue(e.target.value)}
                        data-testid="input-challenge-goal-value"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Goal Unit</Label>
                      <Input
                        placeholder="days"
                        value={challengeGoalUnit}
                        onChange={(e) => setChallengeGoalUnit(e.target.value)}
                        data-testid="input-challenge-goal-unit"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Reward Amount (cents)</Label>
                    <Input
                      type="number"
                      placeholder="2500"
                      value={challengeRewardAmount}
                      onChange={(e) => setChallengeRewardAmount(e.target.value)}
                      data-testid="input-challenge-reward-amount"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={challengeStartDate}
                        onChange={(e) => setChallengeStartDate(e.target.value)}
                        data-testid="input-challenge-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={challengeEndDate}
                        onChange={(e) => setChallengeEndDate(e.target.value)}
                        data-testid="input-challenge-end-date"
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreateChallenge(false)} data-testid="button-cancel-challenge">Cancel</Button>
                  <Button
                    disabled={createChallengeMutation.isPending || !challengeTitle.trim() || !challengeDescription.trim() || !challengeGoalValue}
                    data-testid="button-submit-challenge"
                    onClick={() => {
                      createChallengeMutation.mutate({
                        title: challengeTitle,
                        description: challengeDescription,
                        type: challengeType,
                        goalType: challengeGoalType,
                        goalValue: parseInt(challengeGoalValue) || 0,
                        goalUnit: challengeGoalUnit,
                        rewardAmount: parseInt(challengeRewardAmount) || 0,
                        startDate: new Date(challengeStartDate).toISOString(),
                        endDate: new Date(challengeEndDate).toISOString(),
                        isActive: true,
                      });
                    }}
                  >
                    {createChallengeMutation.isPending ? "Creating..." : "Create Challenge"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              <div className="space-y-3">
                {challenges.map((challenge: any) => (
                  <div key={challenge.id} className="flex items-center justify-between gap-2 p-3 rounded-lg bg-muted/50" data-testid={`challenge-item-${challenge.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{challenge.title}</p>
                        <Badge variant={challenge.isActive ? "default" : "secondary"}>
                          {challenge.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {challenge.goalValue} {challenge.goalUnit} · {formatCents(challenge.rewardAmount || 0)} reward
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{challenge.currentParticipants || 0} joined</Badge>
                      <Button variant="ghost" size="sm" data-testid={`button-edit-challenge-${challenge.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5" />
              Achievement Definitions
            </CardTitle>
            <Dialog open={showCreateAchievement} onOpenChange={setShowCreateAchievement}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="button-new-achievement">
                  <Plus className="w-4 h-4 mr-2" />
                  New Achievement
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="dialog-create-achievement">
                <DialogHeader>
                  <DialogTitle>Create New Achievement</DialogTitle>
                  <DialogDescription>Configure a new achievement definition</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Achievement Name</Label>
                    <Input
                      placeholder="e.g., First Payment"
                      value={achievementName}
                      onChange={(e) => setAchievementName(e.target.value)}
                      data-testid="input-achievement-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      placeholder="Describe the achievement..."
                      value={achievementDescription}
                      onChange={(e) => setAchievementDescription(e.target.value)}
                      data-testid="input-achievement-description"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={achievementCategory} onValueChange={setAchievementCategory}>
                        <SelectTrigger data-testid="select-achievement-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="payment">Payment</SelectItem>
                          <SelectItem value="engagement">Engagement</SelectItem>
                          <SelectItem value="referral">Referral</SelectItem>
                          <SelectItem value="health">Health</SelectItem>
                          <SelectItem value="community">Community</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Badge Level</Label>
                      <Select value={achievementBadgeLevel} onValueChange={setAchievementBadgeLevel}>
                        <SelectTrigger data-testid="select-achievement-badge-level">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bronze">Bronze</SelectItem>
                          <SelectItem value="silver">Silver</SelectItem>
                          <SelectItem value="gold">Gold</SelectItem>
                          <SelectItem value="platinum">Platinum</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Requirement Type</Label>
                      <Select value={achievementRequirementType} onValueChange={setAchievementRequirementType}>
                        <SelectTrigger data-testid="select-achievement-requirement-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="count">Count</SelectItem>
                          <SelectItem value="amount">Amount</SelectItem>
                          <SelectItem value="streak">Streak</SelectItem>
                          <SelectItem value="milestone">Milestone</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Requirement Value</Label>
                      <Input
                        type="number"
                        placeholder="1"
                        value={achievementRequirementValue}
                        onChange={(e) => setAchievementRequirementValue(e.target.value)}
                        data-testid="input-achievement-requirement-value"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Requirement Unit</Label>
                      <Input
                        placeholder="e.g., payments"
                        value={achievementRequirementUnit}
                        onChange={(e) => setAchievementRequirementUnit(e.target.value)}
                        data-testid="input-achievement-requirement-unit"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Reward (cents)</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={achievementRewardAmount}
                        onChange={(e) => setAchievementRewardAmount(e.target.value)}
                        data-testid="input-achievement-reward-amount"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Display Order</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={achievementDisplayOrder}
                        onChange={(e) => setAchievementDisplayOrder(e.target.value)}
                        data-testid="input-achievement-display-order"
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreateAchievement(false)} data-testid="button-cancel-achievement">Cancel</Button>
                  <Button
                    disabled={createAchievementMutation.isPending || !achievementName.trim() || !achievementDescription.trim() || !achievementRequirementValue}
                    data-testid="button-submit-achievement"
                    onClick={() => {
                      createAchievementMutation.mutate({
                        name: achievementName,
                        description: achievementDescription,
                        category: achievementCategory,
                        requirementType: achievementRequirementType,
                        requirementValue: parseInt(achievementRequirementValue) || 0,
                        requirementUnit: achievementRequirementUnit,
                        rewardAmount: parseInt(achievementRewardAmount) || 0,
                        badgeLevel: achievementBadgeLevel,
                        displayOrder: parseInt(achievementDisplayOrder) || 0,
                        isActive: true,
                      });
                    }}
                  >
                    {createAchievementMutation.isPending ? "Creating..." : "Create Achievement"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              <div className="space-y-3">
                {achievements.map((achievement: any) => (
                  <div key={achievement.id} className="flex items-center justify-between gap-2 p-3 rounded-lg bg-muted/50" data-testid={`achievement-item-${achievement.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{achievement.name}</p>
                        <Badge variant="outline" className="capitalize">{achievement.badgeLevel}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {achievement.category} · {achievement.requirementValue} {achievement.requirementUnit}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{formatCents(achievement.rewardAmount || 0)}</Badge>
                      <Button variant="ghost" size="sm" data-testid={`button-edit-achievement-${achievement.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Ambassador Management */}
      <Card data-testid="card-ambassador-management">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users2 className="w-5 h-5" />
            Ambassador Applications
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(!ambassadors || ambassadors.length === 0) ? (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="ambassador-empty-state">No ambassador applications yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="table-ambassadors">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Applied At</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Referral Code</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ambassadors.map((amb: any) => (
                    <TableRow key={amb.id} data-testid={`ambassador-row-${amb.id}`}>
                      <TableCell className="font-medium">{amb.displayName || amb.userId?.slice(0, 8)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {amb.createdAt ? new Date(amb.createdAt).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell>
                        {amb.status === "approved" ? (
                          <Badge variant="default" data-testid={`badge-amb-status-${amb.id}`}>Approved</Badge>
                        ) : amb.status === "denied" ? (
                          <Badge variant="destructive" data-testid={`badge-amb-status-${amb.id}`}>Denied</Badge>
                        ) : amb.status === "suspended" ? (
                          <Badge variant="outline" data-testid={`badge-amb-status-${amb.id}`}>Suspended</Badge>
                        ) : (
                          <Badge variant="secondary" data-testid={`badge-amb-status-${amb.id}`}>Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell className="capitalize">{amb.tier || "bronze"}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {amb.referralCodeId ? amb.referralCodeId.slice(0, 12) + "…" : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {amb.status === "pending" && (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => ambassadorMutation.mutate({ id: amb.id, status: "denied" })}
                              disabled={ambassadorMutation.isPending}
                              data-testid={`button-deny-ambassador-${amb.id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Deny
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => ambassadorMutation.mutate({ id: amb.id, status: "approved" })}
                              disabled={ambassadorMutation.isPending}
                              data-testid={`button-approve-ambassador-${amb.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                          </div>
                        )}
                        {amb.status !== "pending" && (
                          <span className="text-xs text-muted-foreground">
                            {amb.approvedAt ? `Approved ${new Date(amb.approvedAt).toLocaleDateString()}` : amb.status}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-3">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5" />
              Referral Code Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Referrer Bonus</TableHead>
                    <TableHead>Referee Bonus</TableHead>
                    <TableHead>Times Used</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referralCodes.map((code: any) => (
                    <TableRow key={code.id} data-testid={`admin-referral-row-${code.id}`}>
                      <TableCell className="font-mono">{code.code}</TableCell>
                      <TableCell>{code.userId?.slice(0, 8)}...</TableCell>
                      <TableCell>{formatCents(code.referrerBonus || 0)}</TableCell>
                      <TableCell>{formatCents(code.refereeBonus || 0)}</TableCell>
                      <TableCell>{code.timesUsed || 0}</TableCell>
                      <TableCell>
                        <Badge variant={code.isActive ? "default" : "secondary"}>
                          {code.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Top Referrers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-80">
              <div className="space-y-3">
                {[...referralCodes]
                  .sort((a: any, b: any) => (b.timesUsed || 0) - (a.timesUsed || 0))
                  .slice(0, 10)
                  .map((code: any, index: number) => (
                    <div key={code.id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-muted/50" data-testid={`leaderboard-entry-${index}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-muted-foreground w-5">#{index + 1}</span>
                        <div>
                          <p className="text-sm font-medium font-mono">{code.code}</p>
                          <p className="text-xs text-muted-foreground">{code.userId?.slice(0, 8)}...</p>
                        </div>
                      </div>
                      <Badge variant="outline">{code.timesUsed || 0} referrals</Badge>
                    </div>
                  ))}
                {referralCodes.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No referral codes yet</p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
